/* eslint-disable no-prototype-builtins */
import React, { useMemo } from 'react';
import { FILTER_FIELDS, FILTER_OPTIONS } from '_constants/old/filters';
import { CheckedList } from 'modules/Modals/old/FilterModal/components/CheckboxGroup';
import { CollapsedTab } from 'modules/Modals/old/FilterModal/components/CollapsedTab';
import DatePickerGroup from 'modules/Modals/old/FilterModal/components/DatePickerGroup';
import { dropEmptyFilterSections } from 'modules/Modals/old/FilterModal/utils/dropEmptyFilterSections';
import { useLocation } from 'react-router-dom';
import { IsAzAccount, IsMassAccount, IsVaAccount } from 'modules';
import { useHiddenFilters, FILTER_PAGES } from 'hooks';
import * as Styled from './styles';

// Exposed so VisitsQueryBuilder can tell whether this tab has any visible content and hide the
// whole "Visit Characteristics and Outcomes" tab when it would be empty.
export const useVisitCharacteristicsTabs = () => {
  const isMassAccount = IsMassAccount();
  const isAzAccount = IsAzAccount();
  const isVaAccount = IsVaAccount();
  const { pathname } = useLocation();
  const isAssesment = pathname?.includes('/assessment');
  // P20M-1308 — hide Age verification and Other visit details unless the user has the matching
  // permission. Inspection outcome is not part of the hide list, so it stays.
  // Age verification and Other visit details (Product/Sale) are enforcement-list filters.
  const { canViewAgeVerification, canViewOtherVisitDetails } = useHiddenFilters(FILTER_PAGES.ENFORCEMENT);
  const VISIT_CHARACTERISTICS_TABS_BASE = [
    {
      type: 'multiple',
      title: 'Inspection outcome',
      id: 1,
      collapsed: [
        {
          id: 2,
          field: FILTER_FIELDS.VIOLATION,
          title: 'Violation',
          options: FILTER_OPTIONS.VISIT_VIOLATION_OPTIONS,
        },
        !isAzAccount && {
          id: 3,
          title: 'Citation',
          collapsed: [
            {
              field: FILTER_FIELDS.CITATION_CLERK,
              options: FILTER_OPTIONS.CITATION_CLERK_OPTIONS,
              id: 4,
            },
            {
              field: FILTER_FIELDS.CITATION_RETAILER,
              options: FILTER_OPTIONS.CITATION_RETAILER_OPTIONS,
              id: 5,
            },
          ],
        },
        !isAzAccount && {
          id: 6,
          field: FILTER_FIELDS.SALE_TO_MINOR,
          title: 'Youth / Minor',
          options: FILTER_OPTIONS.VISIT_SALE_TO_MINOR_OPTIONS,
        },
        {
          id: 7,
          field: FILTER_FIELDS.FDA_DECISION,
          title: 'FDA',
          options: FILTER_OPTIONS.VISIT_FDA_OPTIONS,
        },
      ].filter(Boolean),
    },
    canViewAgeVerification && {
      type: 'double',
      title: 'Age verification',
      id: 8,
      collapsed: [
        !isAzAccount && {
          id: 9,
          field: FILTER_FIELDS.ID_BASED,
          options: FILTER_OPTIONS.ID_BASED_OPTIONS,
        },
        {
          id: 10,
          field: FILTER_FIELDS.ASK_AGE,
          options: FILTER_OPTIONS.ASK_AGE_OPTIONS,
        },
        {
          id: 11,
          field: FILTER_FIELDS.ASKED_FOR_ID,
          options: FILTER_OPTIONS.ASKED_FOR_ID_OPTIONS,
        },
        {
          id: 12,
          field: FILTER_FIELDS.PRESENTED_ID,
          options: FILTER_OPTIONS.PRESENTED_ID_OPTIONS,
        },
        {
          id: 13,
          field: FILTER_FIELDS.EXAMINED_ID,
          options: FILTER_OPTIONS.EXAMINED_ID_OPTIONS,
        },
        {
          id: 14,
          field: FILTER_FIELDS.AGE_VERIFY_EQUIP,
          options: FILTER_OPTIONS.AGE_VERIFY_EQUIP_OPTIONS,
        },
        {
          id: 15,
          field: FILTER_FIELDS.AGE_VERIFY_USED,
          options: FILTER_OPTIONS.AGE_VERIFY_USED_OPTIONS,
        },
      ].filter(Boolean),
    },
    canViewOtherVisitDetails && {
      type: 'multiple',
      title: 'Other visit details',
      id: 16,
      collapsed: [
        {
          id: 17,
          field: FILTER_FIELDS.TOBACCO_PROD,
          title: 'Product',
          options: FILTER_OPTIONS.TOBACCO_PROD_OPTIONS,
        },
        !isAzAccount && {
          id: 18,
          title: 'Sale',
          collapsed: [
            {
              field: FILTER_FIELDS.TOBACCO_MARKETED,
              options: FILTER_OPTIONS.TOBACCO_MARKETED_OPTIONS,
              id: 19,
            },
            {
              field: FILTER_FIELDS.RECEIPT,
              options: FILTER_OPTIONS.RECEIPT_OPTIONS,
              id: 20,
            },
          ],
        },
      ].filter(Boolean),
    },
  ];

  const VISIT_CHARACTERISTICS_TABS = useMemo(() => {
    const additionalFilters = [];

    if (isMassAccount) {
      additionalFilters.push(
        {
          id: 21,
          field: FILTER_FIELDS.VIOLATION_TYPE,
          title: 'Violation type',
          options: FILTER_OPTIONS.VISIT_VIOLATION_TYPE_OPTIONS,
          type: 'single',
        },
        {
          id: 22,
          field: FILTER_FIELDS.ACTION_TYPE,
          title: 'Action type',
          options: FILTER_OPTIONS.VISIT_ACTION_TYPE_OPTIONS,
          type: 'single',
        },
        {
          id: 23,
          field: FILTER_FIELDS.VIOLATION_OPEN_STATUS,
          title: 'Violation case status',
          options: FILTER_OPTIONS.VIOLATION_OPEN_STATUS_OPTIONS,
          type: 'single',
        },
        {
          id: 24,
          field: FILTER_FIELDS.VIOLATION_CLOSED_DATE,
          title: 'Violation case closed date',
          type: 'calendars',
        },
      );
    }
    if (isVaAccount) {
      additionalFilters.push({
        type: 'double',
        title: 'Gambling',
        id: 25,
        collapsed: [
          {
            id: 26,
            field: FILTER_FIELDS.SELLS_TICKETS,
            options: FILTER_OPTIONS.SELLS_TICKETS_OPTIONS,
          },
          {
            id: 27,
            field: FILTER_FIELDS.HAS_GAMING_MACHINES,
            options: FILTER_OPTIONS.HAS_GAMING_MACHINES_OPTIONS,
          },
        ],
      });
    }

    if (isVaAccount && isAssesment) {
      additionalFilters.push({
        type: 'multiple',
        title: 'Hemp-Derived Cannabis Products',
        id: 28,
        collapsed: [
          {
            id: 29,
            title: 'Sales Status',
            field: FILTER_FIELDS.SELLS_HEMP,
            options: FILTER_OPTIONS.SELLS_HEMP_OPTIONS,
          },
          {
            id: 30,
            title: 'Product Types',
            field: FILTER_FIELDS.SELLS_HEMP_TYPES,
            options: FILTER_OPTIONS.SELLS_HEMP_TYPES_OPTIONS,
          },
          {
            id: 31,
            title: 'Outdoor Advertising',
            field: FILTER_FIELDS.HAS_OUTDOOR_AD_HEMP,
            options: FILTER_OPTIONS.HAS_OUTDOOR_AD_HEMP_OPTIONS,
          },
        ],
      });
    }

    return dropEmptyFilterSections(VISIT_CHARACTERISTICS_TABS_BASE.filter(Boolean).concat(additionalFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMassAccount, isAzAccount, isVaAccount, isAssesment, canViewAgeVerification, canViewOtherVisitDetails]);

  return VISIT_CHARACTERISTICS_TABS;
};

function VisitCharacteristics() {
  const VISIT_CHARACTERISTICS_TABS = useVisitCharacteristicsTabs();

  const getElement = (item) => {
    switch (item.type) {
      case 'single':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <CheckedList name={item.field} options={item.options} />
          </CollapsedTab>
        );
      case 'multiple':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <Styled.FiltersContainer>
                <Styled.FiltersTitle>{elem.title}</Styled.FiltersTitle>
                {elem.collapsed ? (
                  elem.collapsed.map((el) => <CheckedList name={el.field} options={el.options} />)
                ) : (
                  <CheckedList name={elem.field} options={elem.options} />
                )}
              </Styled.FiltersContainer>
            ))}
          </CollapsedTab>
        );
      case 'double':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <CheckedList name={elem.field} options={elem.options} />
            ))}
          </CollapsedTab>
        );
      case 'calendars':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <DatePickerGroup name={item.field} title="" />
          </CollapsedTab>
        );
      default:
        return null;
    }
  };

  return <Styled.Wrapper>{VISIT_CHARACTERISTICS_TABS.map((item) => getElement(item))}</Styled.Wrapper>;
}

export default VisitCharacteristics;
